import { fetchJson } from "../../../lib/fetchJson";

const BASE = "/api/vehicles";
const INV = `${BASE}/vehicle-inventory`;

export const DEALER_INVENTORY_SORT_FIELDS = ["oem", "dealer", "city", "total", "onboarded", "unonboarded"];
export const GROUP_BY_DIMENSIONS = ["oem", "dealer", "city"];

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Build a query string, dropping empty values. */
function qs(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/**
 * Common filters accepted by summary / breakdown / group-by / dealer-inventory.
 * oem / dealer / city are slugs; fromDate / toDate are "YYYY-MM-DD".
 */
function filterParams({ oem, dealer, city, search, onboarded, fromDate, toDate } = {}) {
  return { oem, dealer, city, search, onboarded, from_date: fromDate, to_date: toDate };
}

function normalizeCounts(row) {
  return {
    total: toNumber(row?.total),
    onboarded: toNumber(row?.onboarded),
    unonboarded: toNumber(row?.unonboarded),
  };
}

function normalizeRef(ref) {
  return { id: ref?.id ?? null, name: ref?.name || "—", slug: ref?.slug ?? null };
}

/* ─── Summary ──────────────────────────────────────────────────────────── */
// GET /api/vehicles/vehicle-inventory/summary/?oem=&dealer=&city=&search=&from_date=&to_date=
export async function getVehicleInventorySummary({ signal, ...filters } = {}) {
  const data = await fetchJson(`${INV}/summary/${qs(filterParams(filters))}`, { signal });
  return {
    totalVehicles: toNumber(data?.total_vehicles),
    onboardedVehicles: toNumber(data?.onboarded_vehicles),
    unonboardedVehicles: toNumber(data?.unonboarded_vehicles),
    totalOems: toNumber(data?.total_oems),
    totalDealers: toNumber(data?.total_dealers),
    totalCities: toNumber(data?.total_cities),
  };
}

/* ─── Group-by (donut breakdowns) ──────────────────────────────────────── */
/** Which count a breakdown row shows as "Units": "total" | "onboarded" | "unonboarded". */
export const BREAKDOWN_UNITS_FIELD = "unonboarded";

function normalizeGroupRows(rows) {
  if (!Array.isArray(rows)) return [];
  const list = rows.map((row) => {
    const counts = normalizeCounts(row);
    return {
      id: row?.id ?? null,
      name: row?.name || "Unknown",
      slug: row?.slug ?? null,
      ...counts,
      units: counts[BREAKDOWN_UNITS_FIELD],
      // "Others" / "Unassigned" buckets have no slug and can't be filtered on.
      isOthers: !row?.slug,
    };
  });
  return [...list.filter((r) => !r.isOthers), ...list.filter((r) => r.isOthers)];
}

// GET /api/vehicles/vehicle-inventory/group-by/?dimension=oem|dealer|city&limit=&oem=&dealer=&city=&onboarded=
export async function getVehicleInventoryGroupBy({ dimension, limit, signal, ...filters }) {
  if (!GROUP_BY_DIMENSIONS.includes(dimension)) {
    throw new Error(`Invalid dimension "${dimension}". Allowed: ${GROUP_BY_DIMENSIONS.join(", ")}`);
  }
  const data = await fetchJson(
    `${INV}/group-by/${qs({ dimension, limit, ...filterParams(filters) })}`,
    { signal },
  );
  const results = normalizeGroupRows(data?.results);
  return {
    dimension: data?.dimension || dimension,
    count: toNumber(data?.count),
    results,
    totalUnits: results.reduce((acc, r) => acc + r.units, 0),
  };
}

/**
 * All three donut breakdowns from ONE call.
 * GET /api/vehicles/vehicle-inventory/breakdown/?limit=6&oem=&dealer=&city=&from_date=&to_date=
 * Response: { total_unit, last_synced_date, oem: Row[], city: Row[], dealer: Row[] }
 */
export async function getVehicleInventoryBreakdowns({ limit = 6, signal, ...filters } = {}) {
  const data = await fetchJson(
    `${INV}/breakdown/${qs({ limit, ...filterParams(filters) })}`,
    { signal },
  );
  const shape = (rows) => {
    const results = normalizeGroupRows(rows);
    return { results, totalUnits: results.reduce((acc, r) => acc + r.units, 0) };
  };
  const lastSynced = data?.last_synced_date ? new Date(data.last_synced_date) : null;
  return {
    totalUnit: toNumber(data?.total_unit),
    lastSyncedDate: lastSynced && !Number.isNaN(lastSynced.getTime()) ? lastSynced : null,
    oem: shape(data?.oem),
    city: shape(data?.city),
    dealer: shape(data?.dealer),
  };
}

/* ─── Dealer inventory table ───────────────────────────────────────────── */
// GET /api/vehicles/vehicle-inventory/dealer-inventory/
//   ?sort=-total&page=1&page_size=12&oem=&dealer=&city=&from_date=YYYY-MM-DD&to_date=YYYY-MM-DD
export async function getDealerInventory({ sort = "oem", page = 1, pageSize = 12, signal, ...filters } = {}) {
  const field = String(sort).replace(/^-/, "");
  if (!DEALER_INVENTORY_SORT_FIELDS.includes(field)) {
    throw new Error(`Invalid sort "${sort}". Allowed: ${DEALER_INVENTORY_SORT_FIELDS.join(", ")}`);
  }
  const data = await fetchJson(
    `${INV}/dealer-inventory/${qs({
      sort, page, page_size: pageSize, ...filterParams(filters),
    })}`,
    { signal },
  );
  return {
    count: toNumber(data?.count),
    hasNext: Boolean(data?.next),
    hasPrevious: Boolean(data?.previous),
    results: (data?.results || []).map((row) => ({
      oem: normalizeRef(row?.oem),
      dealer: normalizeRef(row?.dealer),
      city: normalizeRef(row?.city),
      ...normalizeCounts(row),
    })),
  };
}

/* ─── Filter option lists ──────────────────────────────────────────────── */
const optionRows = (data) => Array.isArray(data) ? data
  : Array.isArray(data?.data) ? data.data : data?.results || data?.data?.results || [];

/** Slugs are the filter values for every inventory endpoint; never fall back to UUIDs. */
const toOptions = (data) => optionRows(data)
  .filter((row) => row?.slug && row?.is_active !== false)
  .map((row) => ({ value: row.slug, label: row.name || row.slug, slug: row.slug }));

const sortedOptions = (data) => toOptions(data).sort((a, b) => a.label.localeCompare(b.label));

// GET /api/vehicles/oem/ → [{ id, slug, name, description, is_active }]
export async function getOems({ signal } = {}) {
  return sortedOptions(await fetchJson(`${BASE}/oem/`, { signal }));
}

// GET /api/vehicles/dealer/ → [{ id, name, slug, description, is_active }]
export async function getDealers({ signal } = {}) {
  return sortedOptions(await fetchJson(`${BASE}/dealer/`, { signal }));
}

// GET /api/geographies/geography/cities/ → [{ id, name, slug, ... }] (or { data | results: [...] })
export async function getCities({ signal } = {}) {
  const data = await fetchJson("/api/geographies/geography/cities/", { signal });
  return sortedOptions(data);
}

// Future server-side mode: search + numeric page, with { results, next } responses.
// Page sizing is left to the backend; SearchableSelect's pageSize controls local batching.
function optionPageLoader(path) {
  return async ({ search = "", page = 1, signal } = {}) => {
    const data = await fetchJson(`${path}${qs({ search, page })}`, { signal });
    return { options: toOptions(data), hasMore: Boolean(data?.next || data?.data?.next) };
  };
}

export const inventoryOptionLoaders = {
  oems: optionPageLoader(`${BASE}/oem/`),
  dealers: optionPageLoader(`${BASE}/dealer/`),
  cities: optionPageLoader("/api/geographies/geography/cities/"),
};

/* ─── Legacy filter source (dropdown options) ──────────────────────────── */
// GET /api/crm/inventory/ → { rows: [{ oem, dealer, city, ... }] }
// The City dropdown is built from these rows (names, cascading on OEM / Dealer).
export async function getLegacyInventoryFilterRows({ signal } = {}) {
  const data = await fetchJson("/api/crm/inventory/", { signal });
  return (data?.rows || []).map((r) => ({
    oem: r?.oem || "",
    dealer: r?.dealer || "",
    city: r?.city || "",
  }));
}

/* ─── Export (async: initiate → poll → download) ───────────────────────── */
export const EXPORT_FILE_TYPES = ["excel", "csv"];

const EXPORT_POLL_MS = 2000;
const EXPORT_TIMEOUT_MS = 5 * 60 * 1000;
const PENDING_STATES = ["pending", "queued", "started", "processing", "in_progress", "running", "retry"];
const FAILED_STATES = ["failed", "failure", "error", "revoked", "cancelled"];

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    }, { once: true });
  });

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Open a ready-made file URL (e.g. /media/... on another host) as a normal browser download.
 * We navigate instead of fetch(): a cross-origin fetch is blocked by CORS, but a link is not.
 */
function openFileUrl(rawUrl, filename) {
  let href = rawUrl;
  try {
    const url = new URL(rawUrl, window.location.origin);
    // An https page can't load http files (mixed content) — upgrade when needed.
    if (window.location.protocol === "https:" && url.protocol === "http:") url.protocol = "https:";
    href = url.href;
  } catch (_) {
    // keep raw value
  }
  const a = document.createElement("a");
  a.href = href;
  a.download = filename || ""; // honoured for same-origin; otherwise the server's headers decide
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function filenameFrom(response, fallback) {
  const cd = response.headers.get("Content-Disposition") || "";
  const star = cd.match(/filename\*=(?:UTF-8'')?([^;\n]+)/i);
  if (star?.[1]) return decodeURIComponent(star[1].trim().replace(/"/g, ""));
  const plain = cd.match(/filename="?([^";\n]+)"?/i);
  return plain?.[1]?.trim() || fallback;
}

// GET /api/vehicles/vehicle-inventory/download-initiate/?file_type=excel|csv&oem=&dealer=&city=&from_date=&to_date=
export async function initiateInventoryDownload({ fileType, signal, ...filters }) {
  if (!EXPORT_FILE_TYPES.includes(fileType)) {
    throw new Error(`Invalid file type "${fileType}". Allowed: ${EXPORT_FILE_TYPES.join(", ")}`);
  }
  const data = await fetchJson(
    `${INV}/download-initiate/${qs({ file_type: fileType, ...filterParams(filters) })}`,
    { signal },
  );
  const taskId = data?.task_id ?? data?.taskId ?? data?.id;
  if (!taskId) throw new Error(data?.detail || "Export could not be started (no task_id returned).");
  return taskId;
}

/**
 * GET /api/vehicles/vehicle-inventory/download/?task_id=
 * Polls until the file is ready, then saves it. Handles the server answering with:
 *   - the file itself (non-JSON body)               → saved directly
 *   - JSON { status: "pending" | ... }              → keep polling
 *   - JSON { file_url } (with or without status)    → open that URL as a download
 *   - JSON { status: "failed", detail | error }      → throw
 */
async function pollAndSaveDownload(taskId, { fileType, signal, onStatus }) {
  const ext = fileType === "excel" ? "xlsx" : "csv";
  const fallbackName = `vehicle-inventory.${ext}`;
  const started = Date.now();

  for (;;) {
    if (Date.now() - started > EXPORT_TIMEOUT_MS) {
      throw new Error("Export is taking too long. Please try again in a few minutes.");
    }
    let response;
    try {
      response = await fetch(`${INV}/download/${qs({ task_id: taskId })}`, {
        credentials: "include",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        signal,
      });
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      throw new Error("Backend is unreachable — export could not be downloaded.");
    }

    const type = response.headers.get("content-type") || "";
    const isJson = type.includes("application/json");

    if (response.ok && !isJson) {
      saveBlob(await response.blob(), filenameFrom(response, fallbackName));
      return;
    }

    const body = isJson ? await response.json().catch(() => ({})) : {};
    const status = String(body?.status || body?.state || "").toLowerCase();

    if (!response.ok && response.status !== 202) {
      throw new Error(body?.detail || body?.error || `Export failed (${response.status}).`);
    }
    if (FAILED_STATES.includes(status)) {
      throw new Error(body?.detail || body?.error || body?.message || "Export failed on the server.");
    }

    const fileUrl = body?.file_url || body?.url || body?.download_url || body?.file;
    if (fileUrl) {
      const name = String(fileUrl).split("?")[0].split("/").pop() || fallbackName;
      openFileUrl(fileUrl, name);
      return;
    }

    if (response.status === 202 || PENDING_STATES.includes(status) || !status) {
      onStatus?.(status || "pending");
      await sleep(EXPORT_POLL_MS, signal);
      continue;
    }

    throw new Error(body?.detail || `Unexpected export status "${status}".`);
  }
}

/** Full export flow: start the job with the current filters, wait, save the file. */
export async function exportVehicleInventory({ fileType, signal, onStatus, ...filters }) {
  onStatus?.("initiating");
  const taskId = await initiateInventoryDownload({ fileType, signal, ...filters });
  await pollAndSaveDownload(taskId, { fileType, signal, onStatus });
}
